import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import Button from "@atlaskit/button/standard-button";
import CrossIcon from "@atlaskit/icon/glyph/cross";
import AkModal, { ModalTransition, ModalHeader } from "@atlaskit/modal-dialog";

const modalContentStyles = css({
    "height": "100%",
    "width": "100%",
    "position": "relative",
    "iframe": {
        border: "none"
    },
    ".content-modal": {
        padding: "32px 16px",
        overflowY: "scroll",
        maxHeight: "60vh",
        textAlign: "center"
    },

    'div[id="close-modal-button"]': {
        "position": "absolute",
        "top": "-5px",
        "right": "-60px",

        "button": {
            span: {
                color: "#ffffff"
            }
        },

        "@media screen and (max-width: 479px)": {
            "&": {
                display: "none"
            }
        }
    }
});

const closeModalStyles = css({
    "display": "flex",
    "width": "100%",
    "justify-content": "flex-end",

    "button": {
        height: "24px"
    }
});

interface ModalProps {
    isOpen: boolean;
    closeModal: () => void;
    content: (height: string) => React.ReactNode;
    closeIconLabel?: string;
    isContentModal?: boolean;
}

export const Modal = ({
    isOpen,
    closeModal,
    content,
    closeIconLabel,
    isContentModal
}: ModalProps) => {
    const intl = useIntl();

    const getModalSize = () => {
        if (window.innerWidth >= 1024) {
            return { width: "1024px", height: "576" };
        }

        return {
            width: "large", // atlaskit modal starts ignoring the width at smaller sizes
            height: "540" // atlaskit modal starts setting height to 100% at smaller sizes
        };
    };

    return (
        <ModalTransition>
            {isOpen && (
                <AkModal
                    onClose={closeModal}
                    width={getModalSize().width}
                    height={getModalSize().height}
                >
                    {window.innerWidth < 480 && (
                        <ModalHeader>
                            <div css={closeModalStyles}>
                                <Button
                                    appearance="subtle-link"
                                    onClick={closeModal}
                                >
                                    <CrossIcon
                                        label={
                                            closeIconLabel ??
                                            intl.formatMessage({
                                                id: "Modal.closeIconLabel",
                                                defaultMessage: "close modal"
                                            })
                                        }
                                    />
                                </Button>
                            </div>
                        </ModalHeader>
                    )}
                    <div css={modalContentStyles}>
                        <div className={isContentModal ? "content-modal" : ""}>
                            {content(getModalSize().height)}
                        </div>
                        <div id="close-modal-button">
                            <Button
                                appearance="subtle-link"
                                onClick={closeModal}
                            >
                                <CrossIcon
                                    label={
                                        closeIconLabel ??
                                        intl.formatMessage({
                                            id: "Modal.closeIconLabel",
                                            defaultMessage: "close modal"
                                        })
                                    }
                                    size="xlarge"
                                />
                            </Button>
                        </div>
                    </div>
                </AkModal>
            )}
        </ModalTransition>
    );
};
